import { SimpleTreeView } from '@mui/x-tree-view';
import EmptyMessage from 'core/components/EmptyMessage';
import Loading from 'core/components/Loading';
import { isNumeric } from 'core/utils/commonUtils';
import useProductGroups from 'hooks/swr/useProductGroups';
import useResponsive from 'hooks/useResponsive';
import { isNil } from 'lodash';
import { useId, useMemo } from 'react';
import { findGroupById } from '../../core/utils/productGroupUtils';
import CustomTreeItem from './CustomTreeItem';
import TileProductGroupSelect from './TileProductGroupSelect';
import type { SingleSelectTreeViewProps } from '@mui/x-tree-view';
import type { ProductGroup } from 'models/product/productGroup';
import type { FC } from 'react';

export type ProductGroupSelectedNode = Pick<ProductGroup, 'id' | 'parentId' | 'name'>;

export interface ProductGroupSelectProps
  extends Omit<SingleSelectTreeViewProps, 'onSelectedItemsChange' | 'children'> {
  variant?: 'auto' | 'tree' | 'tile';
  onSelectedItemsChange: (node?: ProductGroupSelectedNode) => void;
  dataPostprocessor?: (data: ProductGroup[]) => ProductGroup[];
}

const ProductGroupSelect: FC<ProductGroupSelectProps> = ({
  onSelectedItemsChange,
  dataPostprocessor,
  variant = 'auto',
  ...props
}) => {
  const { data, isLoading } = useProductGroups();
  const componentId = useId();

  const isSmallScreen = useResponsive('down', 'md');

  const modifiedData = useMemo(
    () => dataPostprocessor?.(structuredClone(data) ?? []) ?? data,
    [data, dataPostprocessor]
  );

  const renderTree = (groups: readonly ProductGroup[]): JSX.Element[] =>
    groups.map((group, i) => (
      <CustomTreeItem
        key={`pg-${componentId}-${group?.id}-${i}`}
        itemId={group?.id?.toString()}
        label={group?.name}>
        {group?.groups.length > 0 ? renderTree(group?.groups) : null}
      </CustomTreeItem>
    ));

  const handleOnNodeSelect = (_: unknown, itemIds: string | null): void => {
    const id = isNil(itemIds) || !isNumeric(itemIds) ? undefined : +itemIds;

    if (isNil(id)) {
      onSelectedItemsChange(undefined);
      return;
    }

    const node = findGroupById((modifiedData as ProductGroup[]) ?? [], id)?.group;

    if (isNil(node)) {
      onSelectedItemsChange(undefined);
      return;
    }

    onSelectedItemsChange({
      id: node.id,
      parentId: node.parentId,
      name: node.name,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isSmallScreen && variant !== 'tree') {
    return (
      <TileProductGroupSelect
        groups={modifiedData}
        selectedItems={props.selectedItems}
        onNodeSelect={(id) => {
          handleOnNodeSelect(null, `${id}`);
        }}
      />
    );
  }

  return (
    <SimpleTreeView onSelectedItemsChange={handleOnNodeSelect} {...props}>
      {!isNil(modifiedData) && modifiedData.length > 0 ? (
        renderTree(modifiedData)
      ) : (
        <EmptyMessage />
      )}
    </SimpleTreeView>
  );
};

export default ProductGroupSelect;
